import {
    Box,
    Button,
    Grid,
    List,
    ListItem,
    Typography,
    Divider,
    Card,
    CardContent,
    Chip
} from "@mui/material";
import { useState } from "react";

// Material UI Icons
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BarChartIcon from "@mui/icons-material/BarChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import GetAppIcon from "@mui/icons-material/GetApp";

interface AssetMetrics {
    asset: string;
    profitLoss: number;
    profitLossPercent: number;
    timeHeld: number; // in days
    totalReturn: number;
    sharpeRatio: number;
    maxDrawdown: number;
    volatility: number;
    averageReturn: number;
    winRate: number;
}

interface StrategyMetricsTableProps {
    strategyName?: string;
    benchmarkMetrics: Omit<AssetMetrics, "asset">;
    totalMetrics: Omit<AssetMetrics, "asset">;
    assetMetrics: AssetMetrics[];
}

export const StrategyMetricsTable = ({
    strategyName = "Strategy",
    benchmarkMetrics,
    totalMetrics,
    assetMetrics
}: StrategyMetricsTableProps) => {
    const [csvLoading, setCsvLoading] = useState(false);

    const formatPercent = (value: number) => {
        const sign = value >= 0 ? "+" : "";
        return `${sign}${(value * 100).toFixed(2)}%`;
    };

    const formatPercentAbsolute = (value: number) => {
        // For metrics like volatility and max drawdown - no sign
        return `${(Math.abs(value) * 100).toFixed(2)}%`;
    };

    const formatDecimal = (value: number, decimals: number = 4) => {
        // For raw decimal values (P/L, volatility) - no percentage
        const sign = value >= 0 ? "+" : "";
        return `${sign}${value.toFixed(decimals)}`;
    };

    const formatDecimalAbsolute = (value: number, decimals: number = 4) => {
        // For raw decimal values without sign (volatility)
        return `${Math.abs(value).toFixed(decimals)}`;
    };

    const formatReturnMultiplier = (value: number, decimals: number = 4) => {
        // Format as multiplier: 1 + return value (e.g., 0.05 becomes 1.05)
        return `${(1 + value).toFixed(decimals)}`;
    };

    const formatPercentNoSign = (value: number) => {
        // For win rate - percentage without plus sign
        return `${(value * 100).toFixed(2)}%`;
    };

    const formatNumber = (value: number, decimals: number = 2) => {
        return value.toFixed(decimals);
    };

    const formatDays = (days: number) => {
        if (days < 1) return `${Math.round(days * 24)}h`;
        return `${Math.round(days)}d`;
    };

    const getProfitColor = (value: number) => {
        if (value > 0) return "success.main";
        if (value < 0) return "error.main";
        return "text.secondary";
    };

    const generateCsv = () => {
        if (!assetMetrics || assetMetrics.length === 0) return;

        setCsvLoading(true);

        const headers = [
            'asset',
            'total_return',
            'time_held_days',
            'profit_loss_percent',
            'sharpe_ratio',
            'max_drawdown_percent',
            'volatility',
            'average_return_percent',
            'win_rate_percent'
        ];

        // In generateCsv function, update the TOTAL row
        const totalRow = [
            "TOTAL",
            (1 + totalMetrics.totalReturn).toFixed(4),
            totalMetrics.timeHeld.toFixed(1), // Add total days here
            (totalMetrics.profitLossPercent * 100).toFixed(2),
            totalMetrics.sharpeRatio.toFixed(2),
            (totalMetrics.maxDrawdown * 100).toFixed(2),
            totalMetrics.volatility.toFixed(4),
            (totalMetrics.averageReturn * 100).toFixed(2),
            (totalMetrics.winRate * 100).toFixed(2)
        ];

        const assetRows = assetMetrics.map((metric) => [
            metric.asset,
            (1 + metric.totalReturn).toFixed(4),
            metric.timeHeld.toFixed(1),
            (metric.profitLossPercent * 100).toFixed(2),
            metric.sharpeRatio.toFixed(2),
            (metric.maxDrawdown * 100).toFixed(2),
            metric.volatility.toFixed(4),
            (metric.averageReturn * 100).toFixed(2),
            (metric.winRate * 100).toFixed(2)
        ]);

        const csvContent = [headers, totalRow, ...assetRows]
            .map(row => row.map(cell => `"${cell}"`).join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", `strategy-metrics-${strategyName}-${new Date().toISOString().split("T")[0]}.csv`);
        link.style.visibility = "hidden";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setCsvLoading(false);
    };

    return (
        <Box mt={6} mb={4}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Performance Metrics
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<GetAppIcon />}
                    disabled={csvLoading || assetMetrics.length === 0}
                    size="small"
                    onClick={generateCsv}
                    sx={{ width: 120 }}
                >
                    CSV
                </Button>
            </Box>
            <Card sx={{ boxShadow: 0 }}>
                <CardContent sx={{ paddingX: 0, paddingTop: 0, "&:last-child": { paddingBottom: 0 } }}>

                    {/* Column Labels */}
                    <Grid container alignItems="center" spacing={0} sx={{ paddingX: 2, paddingY: 1, backgroundColor: "action.hover" }}>
                        <Grid item xs={1.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                                Asset
                            </Typography>
                        </Grid>
                        <Grid item xs={1.25}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                                Return
                            </Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <AccessTimeIcon fontSize="small" /> Time
                            </Typography>
                        </Grid>
                        <Grid item xs={1.5}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <TrendingUpIcon fontSize="small" /> P/L
                            </Typography>
                        </Grid>
                        <Grid item xs={1.25}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                                Sharpe
                            </Typography>
                        </Grid>
                        <Grid item xs={1.25}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                                Max DD
                            </Typography>
                        </Grid>
                        <Grid item xs={1.25}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                                Volatility
                            </Typography>
                        </Grid>
                        <Grid item xs={1.25}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                                Avg Return
                            </Typography>
                        </Grid>
                        <Grid item xs={1.25}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                                Win Rate
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Benchmark Row */}
                    <Box
                        sx={{
                            background: (theme) => theme.palette.mode === "dark"
                                ? "linear-gradient(90deg, rgba(255, 213, 79, 0.15) 0%, rgba(255, 193, 7, 0.15) 100%)"
                                : "linear-gradient(90deg, rgba(255, 213, 79, 0.08) 0%, rgba(255, 193, 7, 0.12) 100%)",
                            borderColor: "warning.main"
                        }}
                    >
                        <Box sx={{ paddingX: 2, paddingY: 2 }}>
                            <Grid container alignItems="center" spacing={0}>
                                <Grid item xs={1.5}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 900,
                                            letterSpacing: 0.5,
                                            color: "warning.main"
                                        }}
                                    >
                                        BENCHMARK
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 900,
                                            color: (1 + benchmarkMetrics.totalReturn) >= 1 ? "success.main" : "error.main"
                                        }}
                                    >
                                        {formatReturnMultiplier(benchmarkMetrics.totalReturn)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {benchmarkMetrics.timeHeld.toFixed(0)}d
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.5}>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                        {formatPercent(benchmarkMetrics.profitLossPercent)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                        {formatNumber(benchmarkMetrics.sharpeRatio, 2)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                        {formatPercentAbsolute(benchmarkMetrics.maxDrawdown)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {formatDecimalAbsolute(benchmarkMetrics.volatility)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {formatPercent(benchmarkMetrics.averageReturn)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {formatPercentNoSign(benchmarkMetrics.winRate)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    {/* Total Row */}
                    <Box
                        sx={{
                            background: (theme) => theme.palette.mode === "dark"
                                ? "linear-gradient(90deg, rgba(144, 202, 249, 0.15) 0%, rgba(66, 165, 245, 0.15) 100%)"
                                : "linear-gradient(90deg, rgba(25, 118, 210, 0.08) 0%, rgba(21, 101, 192, 0.12) 100%)",
                            borderTop: 2,
                            borderBottom: 2,
                            borderColor: "primary.main"
                        }}
                    >
                        <Box sx={{ paddingX: 2, paddingY: 2 }}>
                            <Grid container alignItems="center" spacing={0}>
                                <Grid item xs={1.5}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 900,
                                            letterSpacing: 0.5,
                                            color: "primary.main"
                                        }}
                                    >
                                        TOTAL
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 900,
                                            color: (1 + totalMetrics.totalReturn) >= 1 ? "success.main" : "error.main"
                                        }}
                                    >
                                        {formatReturnMultiplier(totalMetrics.totalReturn)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1}>
                                    <Grid item xs={1}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {totalMetrics.timeHeld.toFixed(0)}d
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid item xs={1.5}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 900
                                        }}
                                    >
                                        {formatPercent(totalMetrics.profitLossPercent)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                        {formatNumber(totalMetrics.sharpeRatio, 2)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                        {formatPercentAbsolute(totalMetrics.maxDrawdown)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {formatDecimalAbsolute(totalMetrics.volatility)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {formatPercent(totalMetrics.averageReturn)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={1.25}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {formatPercentNoSign(totalMetrics.winRate)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Divider />

                    {/* Asset Data */}
                    {assetMetrics.length === 0 ? (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                                No asset metrics available.
                            </Typography>
                        </Box>
                    ) : (
                        <List dense sx={{ padding: 0, margin: 0 }}>
                            {assetMetrics.map((metric, index) => (
                                <Box key={metric.asset}>
                                    <ListItem disableGutters sx={{ padding: 0, margin: 0 }}>
                                        <Box
                                            sx={{
                                                width: "100%",
                                                paddingX: 2,
                                                paddingY: 1,
                                                "&:hover": { backgroundColor: "action.hover" }
                                            }}
                                        >
                                            <Grid container alignItems="center" spacing={0}>
                                                {/* Asset */}
                                                <Grid item xs={1.5}>
                                                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                                                        {metric.asset.toUpperCase()}
                                                    </Typography>
                                                </Grid>

                                                {/* Total Return */}
                                                <Grid item xs={1.25}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: (1 + metric.totalReturn) >= 1 ? "success.main" : "error.main" }}
                                                    >
                                                        {formatReturnMultiplier(metric.totalReturn)}
                                                    </Typography>
                                                </Grid>

                                                {/* Time Held */}
                                                <Grid item xs={1}>
                                                    <Typography variant="body2">
                                                        {formatDays(metric.timeHeld)}
                                                    </Typography>
                                                </Grid>

                                                {/* Profit/Loss */}
                                                <Grid item xs={1.5}>
                                                    <Typography variant="body2">
                                                        {formatPercent(metric.profitLossPercent)}
                                                    </Typography>
                                                </Grid>

                                                {/* Sharpe Ratio */}
                                                <Grid item xs={1.25}>
                                                    <Typography variant="body2">
                                                        {formatNumber(metric.sharpeRatio, 2)}
                                                    </Typography>
                                                </Grid>

                                                {/* Max Drawdown */}
                                                <Grid item xs={1.25}>
                                                    <Typography variant="body2">
                                                        {formatPercentAbsolute(metric.maxDrawdown)}
                                                    </Typography>
                                                </Grid>

                                                {/* Volatility */}
                                                <Grid item xs={1.25}>
                                                    <Typography variant="body2">
                                                        {formatDecimalAbsolute(metric.volatility)}
                                                    </Typography>
                                                </Grid>

                                                {/* Average Return */}
                                                <Grid item xs={1.25}>
                                                    <Typography variant="body2">
                                                        {formatPercent(metric.averageReturn)}
                                                    </Typography>
                                                </Grid>

                                                {/* Win Rate */}
                                                <Grid item xs={1.25}>
                                                    <Typography variant="body2">
                                                        {formatPercentNoSign(metric.winRate)}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </ListItem>
                                    {index < assetMetrics.length - 1 && <Divider />}
                                </Box>
                            ))}
                        </List>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};
